import React, { useState } from 'react';

import { Card, Row, Col, Form, Button } from 'react-bootstrap';

const RATING_LABELS = {
  5: 'Excellent',
  4: 'Very Good',
  3: 'Good',
  2: 'Average',
  1: 'Poor'
};

/*
  Review form (star picker + comment) plus the list of customer reviews.
  Rating/comment state and handlers live in the parent page and are passed in
  as props; the star hover-preview is local presentation state.
*/
function ProductReviews({
  reviews,
  rating,
  setRating,
  comment,
  setComment,
  submitReview,
  deleteReview
}) {
  const isAdmin = JSON.parse(localStorage.getItem('userInfo'))?.isAdmin;

  /* Stars light up on hover before committing a rating */
  const [hoverRating, setHoverRating] = useState(0);

  const activeStars = hoverRating || Number(rating) || 0;
  const label = RATING_LABELS[activeStars] || '';

  const average = reviews.length
    ? (
        reviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) /
        reviews.length
      ).toFixed(1)
    : '0.0';

  return (
    <>
      {/* Review Form */}
      <Card className="border-0 mt-5 pr-form-card">
        <div className="pr-header">
          <span className="pr-header-blob" />

          <div className="pr-header-content">
            <span className="pr-header-icon">⭐</span>
            <div>
              <h3 className="mb-1 fw-bold">Write Your Review</h3>
              <small className="pr-header-sub">
                Share your experience and help other shoppers
              </small>
            </div>
          </div>
        </div>

        <Card.Body className="p-3 p-md-4">
          <Row className="g-4">
            <Col md={5} lg={4}>
              <Form.Label className="fw-bold mb-2">Your Rating</Form.Label>

              <div
                className="pr-star-picker mb-2"
                onMouseLeave={() => setHoverRating(0)}
              >
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    type="button"
                    key={star}
                    className={`pr-star${star <= activeStars ? ' is-on' : ''}`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    aria-label={`${star} star`}
                  >
                    ★
                  </button>
                ))}
              </div>

              {label && (
                <span className={`pr-rating-label lvl-${activeStars}`}>
                  {label}
                </span>
              )}
            </Col>

            <Col md={7} lg={8}>
              <Form.Label className="fw-bold mb-2">Your Comment</Form.Label>

              <Form.Control
                as="textarea"
                rows={4}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience with this product..."
                className="pr-textarea"
              />

              <div className="text-end mt-1">
                <small
                  className={`pr-counter${
                    comment.length > 450 ? ' is-warn' : ''
                  }`}
                >
                  {comment.length}/500
                </small>
              </div>
            </Col>
          </Row>

          <div className="mt-3 d-grid d-sm-flex justify-content-sm-end">
            <Button className="fw-bold px-5 pr-submit" onClick={submitReview}>
              🚀 Submit Review
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Reviews List */}
      <Card className="border-0 mt-4 pr-list-card">
        <Card.Body className="p-3 p-md-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <h3 className="fw-bold mb-0">📝 Customer Reviews</h3>

            {reviews.length > 0 && (
              <span className="pr-avg-chip">
                ⭐ {average} · {reviews.length}{' '}
                {reviews.length === 1 ? 'review' : 'reviews'}
              </span>
            )}
          </div>

          {reviews.length === 0 ? (
            <div className="pr-empty text-center py-5">
              <div className="pr-empty-icon">🔔</div>
              <h5 className="fw-bold mt-2 mb-1">No Reviews Yet</h5>
              <p className="text-muted mb-0">
                Be the first customer to review this product.
              </p>
            </div>
          ) : (
            reviews.map((review) => (
              <Card key={review._id} className="border-0 mb-3 pr-review-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="pr-avatar">
                        {review.userName?.charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <h6 className="fw-bold mb-0">{review.userName}</h6>
                        <small className="text-muted">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </small>
                      </div>
                    </div>

                    <span className="pr-review-stars">
                      {'★'.repeat(Number(review.rating) || 0)}
                      <span className="pr-review-stars-off">
                        {'★'.repeat(5 - (Number(review.rating) || 0))}
                      </span>
                    </span>
                  </div>

                  <p className="pr-review-text mt-3 mb-0">{review.comment}</p>

                  {isAdmin && (
                    <div className="text-end mt-2">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteReview(review._id)}
                      >
                        🗑 Delete
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </Card.Body>
      </Card>

      <style>{reviewStyles}</style>
    </>
  );
}

const reviewStyles = `
  .pr-form-card,
  .pr-list-card {
    border-radius: 26px !important;
    overflow: hidden;
    background: linear-gradient(160deg, #ffffff, #fffaf3) !important;
    box-shadow: 0 20px 50px rgba(124,45,18,0.12) !important;
  }

  /* Header */
  .pr-header {
    position: relative;
    overflow: hidden;
    padding: 26px 24px;
    color: #fff;
    background: linear-gradient(120deg, #3b0764, #7c2d12 55%, #ea580c);
  }
  /* Lamplight wandering slowly behind the banner */
  .pr-header-blob {
    position: absolute;
    width: 260px; height: 260px;
    border-radius: 50%;
    background: #f59e0b;
    filter: blur(70px);
    opacity: 0.4;
    top: -140px; right: -60px;
    pointer-events: none;
    animation: prLampDrift 22s ease-in-out infinite;
  }
  @keyframes prLampDrift {
    0%, 100% { transform: translate(0, 0) scale(1);            opacity: 0.35; }
    50%      { transform: translate(-58vw, 46px) scale(1.25);  opacity: 0.6; }
  }

  /* Beaded fringe hanging along the lower edge, swaying in a draught */
  .pr-header::after {
    content: '';
    position: absolute;
    left: -12px; right: -12px;
    bottom: 0;
    height: 13px;
    pointer-events: none;
    background-image: radial-gradient(circle at 9px 1px, rgba(253,230,138,0.9) 7px, transparent 7.6px);
    background-size: 18px 13px;
    background-repeat: repeat-x;
    animation: prJhalar 3.8s ease-in-out infinite;
  }
  @keyframes prJhalar {
    0%, 100% { transform: translateX(-4px); }
    50%      { transform: translateX(4px); }
  }
  .pr-header-content {
    position: relative;
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .pr-header-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 54px; height: 54px;
    flex-shrink: 0;
    border-radius: 50%;
    font-size: 1.5rem;
    background: rgba(255,255,255,0.16);
    border: 1px solid rgba(253,230,138,0.5);
    box-shadow: 0 6px 18px rgba(0,0,0,0.2);
  }
  /* Aureole — a fan of gold rays turning around the medallion, cleared in
     the middle by the mask so the star itself stays legible */
  .pr-header-icon::before {
    content: '';
    position: absolute;
    inset: -13px;
    border-radius: 50%;
    pointer-events: none;
    background: repeating-conic-gradient(from 0deg, rgba(253,230,138,0.75) 0deg 2.4deg, transparent 2.4deg 15deg);
    -webkit-mask: radial-gradient(circle, transparent 44%, #000 54%, #000 74%, transparent 86%);
    mask: radial-gradient(circle, transparent 44%, #000 54%, #000 74%, transparent 86%);
    animation: prAureole 20s linear infinite;
  }
  @keyframes prAureole {
    to { transform: rotate(360deg); }
  }

  .pr-header h3 { font-size: clamp(1.3rem, 3.5vw, 1.7rem); }
  .pr-header-sub { opacity: 0.85; }

  /* Star picker */
  .pr-star-picker {
    display: flex;
    gap: 6px;
  }
  .pr-star {
    background: none;
    border: none;
    padding: 0;
    line-height: 1;
    cursor: pointer;
    color: #e5e7eb;
    font-size: clamp(1.9rem, 6vw, 2.4rem);
    transition: transform 0.18s ease, color 0.18s ease, filter 0.18s ease;
  }
  .pr-star:hover { transform: scale(1.22) rotate(-6deg); }
  .pr-star.is-on {
    color: #f59e0b;
    filter: drop-shadow(0 4px 8px rgba(245,158,11,0.45));
  }

  /* Colour-coded rating label */
  .pr-rating-label {
    display: inline-block;
    border-radius: 30px;
    padding: 6px 16px;
    font-size: 0.82rem;
    font-weight: 800;
    color: #fff;
    animation: prPop 0.25s ease;
  }
  .pr-rating-label.lvl-5 { background: linear-gradient(135deg, #16a34a, #15803d); }
  .pr-rating-label.lvl-4 { background: linear-gradient(135deg, #65a30d, #4d7c0f); }
  .pr-rating-label.lvl-3 { background: linear-gradient(135deg, #f59e0b, #d97706); }
  .pr-rating-label.lvl-2 { background: linear-gradient(135deg, #f97316, #ea580c); }
  .pr-rating-label.lvl-1 { background: linear-gradient(135deg, #ef4444, #dc2626); }
  @keyframes prPop {
    from { transform: scale(0.85); opacity: 0; }
    to   { transform: scale(1); opacity: 1; }
  }

  /* Textarea */
  .pr-textarea {
    border-radius: 16px !important;
    border: 1px solid #e5e7eb !important;
    padding: 14px !important;
    background: #fff !important;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .pr-textarea:focus {
    border-color: #ea580c !important;
    box-shadow: 0 0 0 0.2rem rgba(234,88,12,0.15) !important;
  }
  .pr-counter { color: #9ca3af; font-weight: 600; }
  .pr-counter.is-warn { color: #ea580c; font-weight: 800; }

  /* Submit */
  .pr-submit {
    border: none;
    border-radius: 50px;
    padding: 13px 20px;
    color: #3b0764;
    background: linear-gradient(135deg, #fde68a, #fbbf24 55%, #f59e0b);
    box-shadow: 0 10px 24px rgba(245,158,11,0.35);
    transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
  }
  .pr-submit:hover {
    color: #3b0764;
    transform: translateY(-3px);
    filter: brightness(1.05);
    box-shadow: 0 16px 32px rgba(245,158,11,0.5);
  }
  .pr-submit:active { transform: scale(0.98); }

  /* Reviews list */
  .pr-avg-chip {
    font-size: 0.82rem;
    font-weight: 800;
    color: #7c2d12;
    background: linear-gradient(135deg, #fde68a, #fbbf24);
    padding: 7px 16px;
    border-radius: 30px;
    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
    white-space: nowrap;
  }
  /* Temple bell waiting to be rung — a peal that decays, then rests,
     with a ring of sound spreading out on each strike */
  .pr-empty-icon {
    position: relative;
    display: inline-block;
    font-size: 3rem;
    line-height: 1;
    transform-origin: 50% 12%;
    animation: prBellPeal 4.4s ease-in-out infinite;
  }
  @keyframes prBellPeal {
    0%             { transform: rotate(0deg); }
    6%             { transform: rotate(17deg); }
    13%            { transform: rotate(-13deg); }
    20%            { transform: rotate(9deg); }
    27%            { transform: rotate(-6deg); }
    34%            { transform: rotate(3deg); }
    41%            { transform: rotate(-1.5deg); }
    48%, 100%      { transform: rotate(0deg); }
  }
  .pr-empty-icon::after {
    content: '';
    position: absolute;
    left: 50%; top: 50%;
    width: 62px; height: 62px;
    margin: -31px 0 0 -31px;
    border-radius: 50%;
    border: 2px solid rgba(245,158,11,0.65);
    pointer-events: none;
    animation: prBellRipple 4.4s ease-out infinite;
  }
  @keyframes prBellRipple {
    0%        { transform: scale(0.6); opacity: 0.9; }
    35%, 100% { transform: scale(2.1); opacity: 0; }
  }
  .pr-review-card {
    border-radius: 18px !important;
    background: linear-gradient(160deg, #ffffff, #fffaf3) !important;
    border: 1px solid #f1e4d0 !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .pr-review-card:hover {
    transform: translateY(-3px);
    border-color: rgba(234,88,12,0.35) !important;
    box-shadow: 0 14px 30px rgba(194,65,12,0.14) !important;
  }
  .pr-avatar {
    width: 44px; height: 44px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    box-shadow: 0 5px 14px rgba(234,88,12,0.35);
  }
  .pr-review-stars {
    color: #f59e0b;
    font-size: 1rem;
    letter-spacing: 2px;
    white-space: nowrap;
  }
  .pr-review-stars-off { color: #e5e7eb; }
  .pr-review-text {
    color: #4b5563;
    line-height: 1.7;
  }

  @media (prefers-reduced-motion: reduce) {
    .pr-rating-label,
    .pr-header-blob,
    .pr-header::after,
    .pr-header-icon::before,
    .pr-empty-icon,
    .pr-empty-icon::after { animation: none; }
    .pr-empty-icon::after { opacity: 0; }
  }
`;

export default ProductReviews;
